import React from 'react';
import { useTranslation } from 'react-i18next';
import FoxAvatar from '../illustrations/FoxAvatar.jsx';
import '../illustrations/Illustrations.css';
import './ChatMock.css';

/**
 * Bars for the answer: label | bar | value, each bar sized against the largest.
 * @param {Object} props
 * @param {Array<{label: string, value: string, size: number, highlight?: boolean}>} props.rows
 * @returns {JSX.Element} Bar list
 */
function AnswerBars({ rows }) {
  const largest = Math.max(...rows.map((row) => row.size));

  return (
    <ul className="chat-mock-rows" role="list">
      {rows.map((row) => (
        <li key={row.label} className={`chat-mock-row${row.highlight ? ' is-highlight' : ''}`}>
          <span className="chat-mock-row-label">{row.label}</span>
          <span className="chat-mock-row-track">
            <span className="chat-mock-row-bar" style={{ width: `${(row.size / largest) * 100}%` }} />
          </span>
          <span className="chat-mock-row-value">{row.value}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A drawn chat with Revetall: a question, and the fox's answer with a small bar chart.
 * Built in HTML so questions and answers of any length wrap nicely.
 * @param {Object} props
 * @param {{question: string, answer: string, note: string, rows: Array}} props.chat
 *   Texts from `features.<id>.chat`
 * @returns {JSX.Element} Chat window
 */
function ChatMock({ chat }) {
  const { t } = useTranslation();

  return (
    <div className="chat-mock">
      <div className="chat-mock-header" aria-hidden="true">
        <FoxAvatar className="chat-mock-avatar" />
        <span className="chat-mock-name">{t('chatMock.name')}</span>
        <span className="chat-mock-online" />
      </div>

      <div className="chat-mock-body">
        <p className="chat-mock-question">{chat.question}</p>
        <div className="chat-mock-answer">
          <p className="chat-mock-answer-text">{chat.answer}</p>
          <AnswerBars rows={chat.rows} />
          <p className="chat-mock-note">{chat.note}</p>
        </div>
      </div>

      <div className="chat-mock-input" aria-hidden="true">
        <span>{t('chatMock.placeholder')}</span>
        <span className="chat-mock-send">
          <svg viewBox="0 0 16 16" focusable="false">
            <path className="ill-arrow" d="M3 8 L13 8 M9 4 L13 8 L9 12" />
          </svg>
        </span>
      </div>
    </div>
  );
}

export default ChatMock;
